export function createLava(canvasId: string = 'bubble') {
  let lava0: any;
  const ge1doot: {
    screen: {
      elem: HTMLElement | null;
      callback: (() => void) | null;
      ctx: CanvasRenderingContext2D | null;
      width: number;
      height: number;
      left: number;
      top: number;
      init: (id: string, callback?: (() => void) | null, initRes?: boolean) => any;
      resize: () => void;
    };
  } = {
    screen: {
      elem: null,
      callback: null,
      ctx: null,
      width: 0,
      height: 0,
      left: 0,
      top: 0,
      init: function (this: any, id: string, callback?: (() => void) | null, initRes?: boolean) {
        this.elem = document.getElementById(id) as HTMLElement | null;
        this.callback = callback || null;
        if (this.elem?.tagName === 'CANVAS') this.ctx = (this.elem as HTMLCanvasElement).getContext('2d');
        window.addEventListener(
          'resize',
          function () {
            // @ts-ignore
            this.resize();
          }.bind(this),
          false
        );
        if (this.elem) {
          this.elem.onselectstart = function () {
            return false;
          };
          this.elem.ondrag = function () {
            return false;
          };
        }
        initRes && this.resize();
        return this;
      },
      resize: function (this: any) {
        const o = this.elem;
        if (!o) return;
        this.width = o.offsetWidth;
        this.height = o.offsetHeight;
        let currentO: HTMLElement | null = o;
        for (this.left = 0, this.top = 0; currentO != null; currentO = currentO.offsetParent as HTMLElement | null) {
          this.left += currentO.offsetLeft;
          this.top += currentO.offsetTop;
        }
        if (this.ctx) {
          (this.elem as HTMLCanvasElement).width = this.width;
          (this.elem as HTMLCanvasElement).height = this.height;
        }
        this.callback && this.callback();
      },
    },
  };

  interface Point {
    x: number;
    y: number;
    magnitude: number;
    computed: number;
    force: number;
    add: (p: Point) => Point;
  }

  const Point = function (this: Point, x: number, y: number): Point {
    this.x = x;
    this.y = y;
    this.magnitude = x * x + y * y;
    this.computed = 0;
    this.force = 0;
    return this;
  } as any;

  Point.prototype.add = function (this: Point, p: Point): Point {
    return new (Point as any)(this.x + p.x, this.y + p.y);
  };

  interface Ball {
    vel: Point;
    pos: Point;
    size: number;
    width: number;
    height: number;
    move: () => void;
  }

  const Ball = function (this: Ball, parent: { width: number; height: number; wh: number }): Ball {
    const min = 0.1;
    const max = 1.5;
    this.vel = new (Point as any)(
      (Math.random() > 0.5 ? 1 : -1) * (0.05 + Math.random() * 0.3),
      (Math.random() > 0.5 ? 1 : -1) * (0.05 + Math.random() * 0.3)
    );
    this.pos = new (Point as any)(
      parent.width * 1.1 + Math.random() * parent.width * 1.2,
      parent.height * 1.1 + Math.random() * parent.height * 1.2
    );
    this.size = parent.wh / 10 + (Math.random() * (max - min) + min) * (parent.wh / 10);
    this.width = parent.width;
    this.height = parent.height;
    return this;
  } as any;

  Ball.prototype.move = function (this: Ball): void {
    if (this.pos.x >= this.width - this.size) {
      if (this.vel.x > 0) this.vel.x = -this.vel.x;
      this.pos.x = this.width - this.size;
    } else if (this.pos.x <= this.size) {
      if (this.vel.x < 0) this.vel.x = -this.vel.x;
      this.pos.x = this.size;
    }

    if (this.pos.y >= this.height - this.size) {
      if (this.vel.y > 0) this.vel.y = -this.vel.y;
      this.pos.y = this.height - this.size;
    } else if (this.pos.y <= this.size) {
      if (this.vel.y < 0) this.vel.y = -this.vel.y;
      this.pos.y = this.size;
    }

    this.pos = this.pos.add(this.vel);
  };

  interface LavaLamp {
    step: number;
    width: number;
    height: number;
    wh: number;
    sx: number;
    sy: number;
    paint: boolean;
    metaFill: CanvasGradient;
    plx: number[];
    ply: number[];
    mscases: number[];
    ix: number[];
    grid: Point[];
    balls: Ball[];
    iter: number;
    sign: number;
    computeForce: (x: number, y: number, idx?: number) => number;
    marchingSquares: (next: [number, number, number | boolean]) => [number, number, number] | false;
    renderMetaballs: () => void;
  }

  const LavaLamp = function (
    this: LavaLamp,
    width: number,
    height: number,
    numBalls: number,
    c0: string,
    c1: string
  ): LavaLamp {
    this.step = 1;
    this.width = width;
    this.height = height;
    this.wh = Math.min(width, height);
    this.sx = Math.floor(this.width / this.step);
    this.sy = Math.floor(this.height / this.step);
    this.paint = false;
    this.metaFill = createRadialGradient(width, height, width, c0, c1);
    this.plx = [0, 0, 1, 0, 1, 1, 1, 1, 1, 1, 0, 1, 0, 0, 0, 0];
    this.ply = [0, 0, 0, 0, 0, 0, 1, 0, 0, 1, 1, 1, 0, 1, 0, 1];
    this.mscases = [0, 3, 0, 3, 1, 3, 0, 3, 2, 2, 0, 2, 1, 1, 0];
    this.ix = [1, 0, -1, 0, 0, 1, 0, -1, -1, 0, 1, 0, 0, 1, 1, 0, 0, 0, 1, 1];
    this.grid = [];
    this.balls = [];
    this.iter = 0;
    this.sign = 1;

    for (let i = 0; i < (this.sx + 2) * (this.sy + 2); i++) {
      this.grid[i] = new (Point as any)((i % (this.sx + 2)) * this.step, Math.floor(i / (this.sx + 2)) * this.step);
    }

    for (let k = 0; k < numBalls; k++) {
      this.balls[k] = new (Ball as any)(this);
    }
    return this;
  } as any;
  LavaLamp.prototype.computeForce = function (this: LavaLamp, x: number, y: number, idx?: number): number {
    let force: number;
    const id = idx || x + y * (this.sx + 2);

    if (x === 0 || y === 0 || x === this.sx || y === this.sy) {
      force = 0.6 * this.sign;
    } else {
      force = 0;
      const cell = this.grid[id];

      if (cell) {
        let i = 0;
        let ball: Ball;
        while ((ball = this.balls[i++])) {
          force +=
            (ball.size * ball.size) /
            (-2 * cell.x * ball.pos.x - 2 * cell.y * ball.pos.y + ball.pos.magnitude + cell.magnitude);
        }
      }
      force *= this.sign;
    }

    if (this.grid[id]) {
      this.grid[id].force = force;
    }

    return force;
  };
  LavaLamp.prototype.marchingSquares = function (
    this: LavaLamp,
    next: [number, number, number | boolean]
  ): [number, number, number] | false {
    const x = next[0];
    const y = next[1];
    const pdir = next[2];
    const id = x + y * (this.sx + 2);

    if (!this.grid[id] || this.grid[id].computed === this.iter) {
      return false;
    }

    let dir: number,
      mscase = 0;

    for (var i = 0; i < 4; i++) {
      var idn = x + this.ix[i + 12] + (y + this.ix[i + 16]) * (this.sx + 2);

      if (!this.grid[idn]) {
        continue;
      }

      var force = this.grid[idn].force;
      if ((force > 0 && this.sign < 0) || (force < 0 && this.sign > 0) || !force) {
        force = this.computeForce(x + this.ix[i + 12], y + this.ix[i + 16], idn);
      }
      if (Math.abs(force) > 1) mscase += Math.pow(2, i);
    }
    if (mscase === 15) {
      return [x, y - 1, 0];
    } else {
      if (mscase === 5) dir = (pdir as number) === 2 ? 3 : 1;
      else if (mscase === 10) dir = (pdir as number) === 3 ? 0 : 2;
      else {
        dir = this.mscases[mscase];
        if (this.grid[id]) {
          this.grid[id].computed = this.iter;
        }
      }

      const gridIdx1 = x + this.plx[4 * dir + 2] + (y + this.ply[4 * dir + 2]) * (this.sx + 2);
      const gridIdx2 = x + this.plx[4 * dir + 3] + (y + this.ply[4 * dir + 3]) * (this.sx + 2);
      const gridIdx3 = x + this.plx[4 * dir] + (y + this.ply[4 * dir]) * (this.sx + 2);
      const gridIdx4 = x + this.plx[4 * dir + 1] + (y + this.ply[4 * dir + 1]) * (this.sx + 2);

      if (this.grid[gridIdx1] && this.grid[gridIdx2] && this.grid[gridIdx3] && this.grid[gridIdx4]) {
        const ix =
          this.step /
          (Math.abs(Math.abs(this.grid[gridIdx1].force) - 1) / Math.abs(Math.abs(this.grid[gridIdx2].force) - 1) + 1);
        ctx.lineTo(this.grid[gridIdx3].x + this.ix[dir + 4] * ix, this.grid[gridIdx4].y + this.ix[dir + 4] * ix);
      }
      this.paint = true;
      return [x + this.ix[dir + 4], y + this.ix[dir + 8], dir];
    }
  };

  LavaLamp.prototype.renderMetaballs = function (this: LavaLamp): void {
    let i = 0;
    let ball: Ball;
    while ((ball = this.balls[i++])) ball.move();
    this.iter++;
    this.sign = -this.sign;
    this.paint = false;
    if (ctx) {
      ctx.fillStyle = this.metaFill;
      ctx.beginPath();
      i = 0;
      while ((ball = this.balls[i++])) {
        let next: [number, number, number] = [
          Math.round(ball.pos.x / this.step),
          Math.round(ball.pos.y / this.step),
          0,
        ];
        do {
          const result = this.marchingSquares(next);
          if (!result) break;
          next = result;
        } while (true);
        if (this.paint) {
          ctx.fill();
          ctx.closePath();
          ctx.beginPath();
          this.paint = false;
        }
      }
    }
  };

  const createRadialGradient = function (w: number, h: number, r: number, c0: string, c1: string): CanvasGradient {
    const gradient = ctx.createRadialGradient(w / 1, h / 1, 0, w / 1, h / 1, r);
    gradient.addColorStop(0, c0);
    gradient.addColorStop(1, c1);
    return gradient;
  };

  let frameCount = 0;
  const frameSkip = 1;

  const run = function (): void {
    requestAnimationFrame(run);

    frameCount++;
    if (frameCount % frameSkip !== 0) {
      return;
    }

    ctx.clearRect(0, 0, screen.width, screen.height);
    lava0.renderMetaballs();
  };
  const screen = ge1doot.screen.init(canvasId, null, true);
  const ctx = screen.ctx;
  if (!ctx) {
    console.error('Canvas context not found for id:', canvasId);
    return;
  }
  screen.resize();
  lava0 = new (LavaLamp as any)(screen.width, screen.height, 6, '#B700FF', '#B700FF');

  run();
}
